import { LOCALES } from '@sjuni/types';
import { cn } from '@sjuni/ui';
import { Bell, LogOut, Menu, Search, User } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useEditorLocale } from '@/lib/editorLocale/useEditorLocale';
import { useAuth } from '@/lib/auth/useAuth';

/**
 * docs/05-ADMIN-DASHBOARD.md §1's header:
 *
 *   [seal] SJSU Admin   ⌘K search   🌐 KA|EN   🔔 3   [avatar ▾]
 *
 * The `KA|EN` control is the one that is easy to build wrong. It switches which
 * locale the **editor fields** show — not the interface language, which follows
 * `users.locale`. It is labelled accordingly, so nobody reaches for it expecting
 * the sidebar to change.
 *
 * The bell is present and empty. §1 wants it fed by new contact submissions,
 * scheduled content that just published, and failed jobs — none of which have
 * an endpoint until their modules land, and a count invented from nothing is
 * worse than a count of zero.
 */
export interface HeaderProps {
  onOpenPalette: () => void;
  onOpenDrawer: () => void;
}

export function Header({ onOpenPalette, onOpenDrawer }: HeaderProps) {
  const { t } = useTranslation();
  const { user, signOut } = useAuth();
  const { locale: editorLocale, setLocale: setEditorLocale } = useEditorLocale();

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Click-away and Escape for the avatar menu. Not a `Modal`: it is a menu
  // beside its trigger, and trapping focus in it would be wrong.
  useEffect(() => {
    if (!menuOpen) {
      return;
    }

    function onPointerDown(event: MouseEvent) {
      if (!menuRef.current?.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    }

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setMenuOpen(false);
      }
    }

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-subtle bg-surface px-4 lg:px-6">
      <button
        type="button"
        onClick={onOpenDrawer}
        aria-label={t('nav.openMenu')}
        className="grid h-10 w-10 place-items-center rounded-card text-secondary hover:bg-surface-alt hover:text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy-600 lg:hidden"
      >
        <Menu size={20} strokeWidth={1.75} aria-hidden="true" />
      </button>

      <Link
        to="/"
        className="flex items-center gap-2 rounded-card px-1 py-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy-600"
      >
        {/* Decorative — the name beside it is the link's accessible label,
            and on small screens the `aria-label` on the anchor is not needed
            because the alt text would be the only label. */}
        <img src="/logo.png" alt={t('common.appName')} width={32} height={32} className="h-8 w-8" />
        <span className="hidden text-body font-extrabold text-primary sm:inline">
          {t('common.appName')}
        </span>
      </Link>

      {/*
        A button that opens the palette, not an input. §1 calls it "⌘K search";
        two search boxes on one screen — this and the palette's own — is one
        more than anybody needs.
      */}
      <button
        type="button"
        onClick={onOpenPalette}
        /*
         * Labelled explicitly because the text beside the icon is `sm:inline`:
         * on a phone it collapses to a magnifying glass, and a button whose
         * only name is hidden text has no name at all.
         */
        aria-label={t('nav.palette.open')}
        className="ms-auto flex items-center gap-2 rounded-full bg-surface-alt px-4 py-2 text-body-sm text-secondary transition-colors duration-fast hover:text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy-600"
      >
        <Search size={16} strokeWidth={1.75} aria-hidden="true" />
        <span className="hidden sm:inline">{t('nav.palette.open')}</span>
        <kbd className="hidden rounded border border-subtle px-1.5 py-0.5 text-caption md:inline">
          {t('nav.palette.shortcut')}
        </kbd>
      </button>

      {/* §1's editor-locale toggle. A radiogroup, because it is a choice
          between two states rather than two independent switches. */}
      <div
        role="radiogroup"
        aria-label={t('nav.editorLocale.label')}
        className="flex items-center rounded-full bg-surface-alt p-1"
      >
        {LOCALES.map((locale) => (
          <button
            key={locale}
            type="button"
            role="radio"
            aria-checked={editorLocale === locale}
            onClick={() => setEditorLocale(locale)}
            className={cn(
              'rounded-full px-3 py-1 text-caption font-extrabold uppercase transition-colors duration-fast',
              'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy-600',
              editorLocale === locale
                ? 'bg-surface text-primary shadow-sm'
                : 'text-secondary hover:text-primary',
            )}
          >
            {/* Latin abbreviations — CLAUDE.md rule 3 is about Georgian text,
                which these are not. */}
            {locale}
          </button>
        ))}
      </div>

      <button
        type="button"
        // Disabled rather than hidden: §1 puts it in the header, and an editor
        // should be able to see that notifications are a thing that will
        // appear here. `title` says why it does nothing yet.
        disabled
        title={t('nav.notifications.pending')}
        aria-label={t('nav.notifications.label')}
        className="grid h-10 w-10 place-items-center rounded-card text-muted"
      >
        <Bell size={18} strokeWidth={1.75} aria-hidden="true" />
      </button>

      <div ref={menuRef} className="relative">
        <button
          type="button"
          onClick={() => setMenuOpen((current) => !current)}
          aria-expanded={menuOpen}
          aria-haspopup="menu"
          // Same reason: the name is `md:inline`, and the initials beside it
          // are a decoration rather than a label.
          aria-label={t('nav.accountMenu', { name: user?.name ?? '' })}
          className="flex items-center gap-2 rounded-full py-1 pe-2 ps-1 text-body-sm text-primary hover:bg-surface-alt focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy-600"
        >
          {/*
            The account's own picture when it has one. `alt=""` because the
            button already carries the person's name: `Avatar` would otherwise
            announce it twice.
          */}
          {user?.avatar?.urls.thumb ? (
            <img
              src={user.avatar.urls.thumb}
              alt=""
              width={32}
              height={32}
              className="h-8 w-8 rounded-full object-cover"
            />
          ) : (
            <span className="grid h-8 w-8 place-items-center rounded-full bg-navy-600 text-caption font-extrabold text-white">
              {initials(user?.name ?? '')}
            </span>
          )}
          <span className="hidden max-w-32 truncate md:inline">{user?.name}</span>
        </button>

        {menuOpen ? (
          <div
            role="menu"
            className="absolute end-0 mt-2 w-56 overflow-hidden rounded-card border border-subtle bg-surface py-1 shadow-lg"
          >
            <p className="border-b border-subtle px-4 py-2 text-caption text-muted">
              {user?.email}
            </p>

            <Link
              to="/profile"
              role="menuitem"
              onClick={() => setMenuOpen(false)}
              className="flex items-center gap-2 px-4 py-2 text-body-sm text-secondary hover:bg-surface-alt hover:text-primary"
            >
              <User size={16} strokeWidth={1.75} aria-hidden="true" />
              {t('nav.items.profile')}
            </Link>

            <button
              type="button"
              role="menuitem"
              onClick={() => void signOut()}
              className="flex w-full items-center gap-2 px-4 py-2 text-body-sm text-secondary hover:bg-surface-alt hover:text-primary"
            >
              <LogOut size={16} strokeWidth={1.75} aria-hidden="true" />
              {t('nav.signOut')}
            </button>
          </div>
        ) : null}
      </div>
    </header>
  );
}

/**
 * Two letters for the avatar. `Intl.Segmenter` rather than `slice`, because a
 * Georgian name is multi-byte and slicing a UTF-16 string can cut a character
 * in half.
 */
function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean).slice(0, 2);

  return words.map((word) => [...word][0] ?? '').join('');
}
